import { useCallback, useMemo } from 'react';
import { Box, DataTable, DataTableBody, DataTableFooter } from '@optiaxiom/react';
import { getCoreRowModel, type PaginationState, useReactTable } from '@tanstack/react-table';

import { EmptyPage } from '@frontend/components';
import { AgentExecutionItem } from '@frontend/types/agentExecution';

import createColumns from './columns';
import { executionsTableWrapper } from './ExecutionsTable.css';

type ExecutionsTableProps = {
  executions: AgentExecutionItem[];
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
  isLoading: boolean;
  error: Error | null;
  onExecutionSelect: (execution: AgentExecutionItem) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onWorkflowExecutionView?: (execution: AgentExecutionItem) => void;
};

const isWorkflowExecution = (execution: AgentExecutionItem): boolean => {
  return !!execution.workflow_agent;
};

export function ExecutionsTable({
  executions,
  pagination,
  isLoading,
  error,
  onExecutionSelect,
  onPageChange,
  onPageSizeChange,
  onWorkflowExecutionView,
}: ExecutionsTableProps) {
  const paginationState = useMemo(
    () => ({
      pageIndex: pagination ? pagination.page - 1 : 0,
      pageSize: pagination?.page_size ?? 20,
    }),
    [pagination]
  );

  const handleRowClick = useCallback(
    (execution: AgentExecutionItem) => {
      if (isWorkflowExecution(execution) && onWorkflowExecutionView) {
        onWorkflowExecutionView(execution);
      } else {
        onExecutionSelect(execution);
      }
    },
    [onExecutionSelect, onWorkflowExecutionView]
  );

  const columns = useMemo(() => createColumns(), []);

  const table = useReactTable({
    columns,
    data: executions,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: pagination?.total_count,
    pageCount: pagination?.total_pages,
    state: {
      pagination: paginationState,
    },
    meta: {
      onRowClick: handleRowClick,
    },
    onPaginationChange: (
      updater: PaginationState | ((old: PaginationState) => PaginationState)
    ) => {
      const newPagination = typeof updater === 'function' ? updater(paginationState) : updater;

      if (newPagination.pageIndex !== paginationState.pageIndex) {
        onPageChange(newPagination.pageIndex + 1);
      }
      if (newPagination.pageSize !== paginationState.pageSize && onPageSizeChange) {
        onPageSizeChange(newPagination.pageSize);
      }
    },
  });

  if (error) {
    return <EmptyPage title="Failed to load executions" description={error.message} />;
  }

  if (!isLoading && executions.length === 0) {
    return (
      <EmptyPage
        title="No executions found"
        description="Agent executions will appear here as they are performed."
      />
    );
  }

  return (
    <Box className={executionsTableWrapper}>
      <DataTable table={table}>
        <DataTableBody loading={isLoading} />
        <DataTableFooter showPageSizeOptions />
      </DataTable>
    </Box>
  );
}
